// MySQL `FIND_IN_SET('<cursor>', <set_column>)` argument detector.
//
// `detectComparisonValueContext` only recognises comparison operators whose
// column operand sits to the LEFT of the value. `FIND_IN_SET(needle, haystack)`
// inverts that: the value literal is the FIRST argument and the SET column is
// the SECOND, so the column reference is FORWARD of the cursor. This detector
// confirms the caret is inside the first string-literal argument of a
// `FIND_IN_SET(` call and returns the second argument's column reference, so
// the resolver can surface the column's native SET members there.
//
// Pure string scan — no grammar. Returns the raw (un-folded) dotted column
// text, or `null` when the caret isn't in such a slot. Conservative: requires
// the call's second argument to be present (a bare `FIND_IN_SET('` with no
// `, column` yet yields `null` — there is no column to resolve).

const IDENT_CHAR = /[A-Za-z0-9_$]/;
const DOTTED_IDENT_CHAR = /[A-Za-z0-9_$.`]/;

export function detectFindInSetColumnArg(text: string, offset: number): string | null {
  if (offset < 0 || offset > text.length) return null;

  // 1. Locate the opening quote of the string literal the caret is in. Scan
  //    left over the literal body; bail on a newline (a member literal won't
  //    span lines) so we don't run away across the whole buffer.
  let q = offset - 1;
  while (q >= 0 && text[q] !== "'") {
    if (text[q] === '\n') return null;
    q -= 1;
  }
  if (q < 0) return null;
  const openQuote = q;

  // 2. Immediately before the opening quote (modulo whitespace) must be `(`.
  let k = openQuote - 1;
  while (k >= 0 && isSpace(text[k]!)) k -= 1;
  if (k < 0 || text[k] !== '(') return null;

  // 3. Before the `(` (modulo whitespace) must be the `FIND_IN_SET` identifier.
  let e = k - 1;
  while (e >= 0 && isSpace(text[e]!)) e -= 1;
  let s = e;
  while (s >= 0 && IDENT_CHAR.test(text[s]!)) s -= 1;
  const fn = text.slice(s + 1, e + 1);
  if (fn.toUpperCase() !== 'FIND_IN_SET') return null;

  // 4. The caret must be inside the FIRST argument — i.e. no top-level comma
  //    between the `(` and the opening quote. (Defends against
  //    `FIND_IN_SET(col, '<cursor>')`, where the literal is arg 2 and the
  //    column is arg 1, handled by the existing comparison detector path.)
  for (let p = k + 1; p < openQuote; p += 1) {
    if (text[p] === ',') return null;
  }

  // 5. Read arg 2: from after the closing quote, expect `,` then a dotted
  //    identifier. Honour `''` escaping while skipping the literal body.
  let f = openQuote + 1;
  while (f < text.length && text[f] !== "'") {
    if (text[f] === '\n') return null;
    f += 1;
  }
  // Skip past any `''` escapes inside the literal to its true terminator.
  while (f + 1 < text.length && text[f] === "'" && text[f + 1] === "'") {
    f += 2;
    while (f < text.length && text[f] !== "'") {
      if (text[f] === '\n') return null;
      f += 1;
    }
  }
  if (f >= text.length || text[f] !== "'") return null; // unterminated literal
  f += 1; // past closing quote
  while (f < text.length && isSpace(text[f]!)) f += 1;
  if (text[f] !== ',') return null;
  f += 1;
  while (f < text.length && isSpace(text[f]!)) f += 1;
  let g = f;
  while (g < text.length && DOTTED_IDENT_CHAR.test(text[g]!)) g += 1;
  const col = text.slice(f, g).trim();
  return col.length > 0 ? col : null;
}

function isSpace(ch: string): boolean {
  return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r';
}
